import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ExpandMore from '@mui/icons-material/ExpandMore';

export type ConfigTab = { value: string; label: string; content: React.ReactNode; hasError?: boolean };

/**
 * The configuration column: a tab per concern (settings, criteria). Every tab stays mounted and is
 * only hidden — they hold form fields, and a field that unmounts drops its value and validation.
 */
export default function ConfigPanel({ tabs, label }: { tabs: ConfigTab[]; label: string }) {
  const [active, setActive] = React.useState(tabs[0]?.value ?? '');
  const id = React.useId();
  return (
    <Box
      component='section'
      aria-label={label}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Tabs
        value={active}
        onChange={(_event, next) => setActive(next)}
        sx={{ px: 1, borderBottom: 1, borderColor: 'divider', flex: '0 0 auto' }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            id={`${id}-${tab.value}-tab`}
            aria-controls={`${id}-${tab.value}-panel`}
            label={
              tab.hasError ? (
                <Box component='span' sx={{ color: 'error.main' }}>{`${tab.label} (needs attention)`}</Box>
              ) : (
                tab.label
              )
            }
          />
        ))}
      </Tabs>
      {tabs.map((tab) => (
        <Box
          key={tab.value}
          role='tabpanel'
          id={`${id}-${tab.value}-panel`}
          aria-labelledby={`${id}-${tab.value}-tab`}
          hidden={active !== tab.value}
          sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
        >
          {tab.content}
        </Box>
      ))}
    </Box>
  );
}

/** One collapsible group of settings, with a one-line summary of its values while closed. */
export function ConfigSection({
  title,
  summary,
  defaultExpanded = false,
  hasError = false,
  children,
}: {
  title: string;
  summary?: React.ReactNode;
  defaultExpanded?: boolean;
  hasError?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      square
      elevation={0}
      slotProps={{ transition: { unmountOnExit: false } }}
      sx={{ bgcolor: 'transparent', borderBottom: 1, borderColor: 'divider', '&::before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          flexDirection: 'row-reverse',
          gap: 1,
          px: 1.5,
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, minWidth: 0 },
        }}
      >
        <Typography
          variant='subtitle2'
          component='h3'
          sx={{ fontWeight: 600, flex: '0 0 auto', color: hasError ? 'error.main' : 'text.primary' }}
        >
          {title}
        </Typography>
        {summary && (
          <Typography
            variant='caption'
            color={hasError ? 'error' : 'text.secondary'}
            noWrap
            sx={{ ml: 'auto', minWidth: 0 }}
          >
            {summary}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}
